import axios from 'axios';
import { BUSINESS_CODE } from '../constants/index.js';

/**
 * @typedef {Object} CreateRequestOptions
 * @property {string} [baseURL] - 接口基础路径
 * @property {number} [timeout=15000] - 超时时间（毫秒）
 * @property {() => string} [getBrandNo] - 获取当前品牌编码的函数
 * @property {() => string} [getToken] - 获取 Token 的函数
 * @property {() => string} [getLocale] - 获取当前语言码的函数
 * @property {(config: any) => any} [transformRequest] - 请求数据转化函数
 * @property {(data: any) => any} [transformResponse] - 响应数据转化函数
 * @property {() => void} [onUnauthorized] - 401 回调
 * @property {() => void} [onForbidden] - 403 回调
 */

/**
 * 创建 axios 请求实例
 * 各端按需实例化，通过 options 注入 Brand-No、Token 等
 *
 * 抹平 GET 请求差异：
 *   request.get('/users', { page: 1 })
 *   request.get('/users', { params: { page: 1 } })
 * 以上两种写法等价
 *
 * @param {CreateRequestOptions} [options]
 * @returns {{
 *   get: (url: string, config?: any) => Promise<any>,
 *   post: (url: string, data?: any, config?: any) => Promise<any>,
 *   put: (url: string, data?: any, config?: any) => Promise<any>,
 *   delete: (url: string, config?: any) => Promise<any>,
 *   patch: (url: string, data?: any, config?: any) => Promise<any>,
 *   defaults: any,
 *   interceptors: any,
 * }}
 */
/**
 * 默认请求数据转化（预留接口）
 * 后续可用于参数加密、签名、统一包装等
 * @param {Object} config - axios 请求配置
 * @returns {Object} 转化后的配置
 */
function defaultTransformRequest(config) {
  // TODO: 后续可接入参数加密、请求签名等逻辑
  return config;
}

/**
 * 默认响应数据转化（预留接口）
 * 后续可用于数据解密、统一解包等
 * @param {any} data - 响应数据
 * @returns {any} 转化后的数据
 */
function defaultTransformResponse(data) {
  // TODO: 后续可接入数据解密、统一解包等逻辑
  return data;
}

export function createRequest(options = {}) {
  const instance = axios.create({
    baseURL: options.baseURL || '',
    timeout: options.timeout || 15000,
  });

  // 请求拦截：注入 Brand-No / Authorization / Language + 数据转化
  instance.interceptors.request.use(config => {
    const brandNo = options.getBrandNo?.();
    const token = options.getToken?.();
    const locale = options.getLocale?.();

    if (brandNo) {
      config.headers['Brand-No'] = brandNo;
    }
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (locale) {
      config.headers['Accept-Language'] = locale;
      config.headers['X-Locale'] = locale;
    }

    // 请求数据转化（预留）
    const transformRequest = options.transformRequest || defaultTransformRequest;
    return transformRequest(config);
  });

  // 响应拦截：统一错误处理 + 数据转化
  instance.interceptors.response.use(
    response => {
      let res = response.data;

      // 如果后端有业务 code 约定，在此统一处理
      if (BUSINESS_CODE.LOGOUT.includes(res.code)) {
        // Token 过期或未登录
        options.onUnauthorized?.();
      }

      // 响应数据转化（预留）
      const transformResponse = options.transformResponse || defaultTransformResponse;
      res = transformResponse(res);
      return res;
    },
    error => {
      const status = error.response?.status;
      if (status === 403) {
        // 无权限 / 跨品牌访问被拒绝
        options.onForbidden?.();
      }
      // 非 401/403 的错误（如 500），如果响应体中仍有业务数据，
      // 将数据 resolve 给调用方，由业务层通过 code 判断错误
      const data = error.response?.data;
      if (status !== 401 && status !== 403 && data) {
        return Promise.resolve(data);
      }

      return Promise.reject(error);
    }
  );

  // 包装层：抹平 GET 请求的 params 差异
  const wrapper = config => instance(config);

  wrapper.get = (url, config = {}) => {
    // 如果用户已经使用了 { params: {...} }，保持原样
    // 否则将整个 config 对象自动包进 params
    const hasExplicitParams = Object.prototype.hasOwnProperty.call(config, 'params');
    const normalized = hasExplicitParams ? config : { params: config };
    return instance.get(url, normalized);
  };

  wrapper.post = (url, data, config) => instance.post(url, data, config);
  wrapper.put = (url, data, config) => instance.put(url, data, config);
  wrapper.delete = (url, config) => instance.delete(url, config);
  wrapper.patch = (url, data, config) => instance.patch(url, data, config);

  // 透传常用属性，便于外部扩展
  wrapper.defaults = instance.defaults;
  wrapper.interceptors = instance.interceptors;

  return wrapper;
}
